import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutGrid,
  BarChart3,
  Target,
  Boxes,
  Users,
  ShoppingCart,
  FileText,
  Bell,
  LogOut,
  ShieldCheck,
  ChevronDown,
  Sparkles,
  Activity,
} from "lucide-react";
import PulseScoreBadge from "./PulseScoreBadge";
import { CommandKBadge } from "./CommandPalette";
import { isOwner } from "../lib/useTracking";
import { clearAllQueryCache } from "../lib/queryClient";

const NAV_ITEMS = [
  { to: "/", label: "דף הבית", icon: LayoutGrid, testId: "nav-home" },
  { to: "/insights", label: "תובנות עסקיות", icon: BarChart3, testId: "nav-insights" },
  { to: "/inventory", label: "מלאי", icon: Boxes, testId: "nav-inventory" },
  { to: "/customers", label: "לקוחות", icon: Users, testId: "nav-customers" },
  { to: "/what-changed", label: "מה השתנה", icon: Activity, testId: "nav-what-changed" },
  { to: "/actions", label: "מרכז פעולות", icon: Target, testId: "nav-actions" },
  { to: "/chat", label: "שיחה עם Pulse AI", icon: Sparkles, testId: "nav-chat" },
];

const COMING_SOON_ITEMS = [
  { to: "/procurement", label: "רכש", icon: ShoppingCart, testId: "nav-procurement" },
  { to: "/reports", label: "דוחות", icon: FileText, testId: "nav-reports" },
  { to: "/alerts", label: "התראות", icon: Bell, testId: "nav-alerts" },
];

export default function Sidebar({ onNavigate }) {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("pulse_user") || "{}");
  const owner = isOwner();
  const [soonOpen, setSoonOpen] = useState(false);

  const handleLogout = () => {
    // Wipe React Query cache BEFORE clearing the token so no stale tenant
    // data sticks around for the next user who logs in on this device.
    try {
      clearAllQueryCache();
    } catch {
      /* React Query optional — if not initialised yet, skip silently */
    }
    localStorage.removeItem("pulse_token");
    localStorage.removeItem("pulse_user");
    navigate("/login");
  };

  const renderItem = (item) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.to === "/"}
      onClick={(e) => {
        // Hash links (e.g. /insights#branches): ensure we navigate even when
        // already on the base path, so the page can re-scroll to the anchor.
        if (item.to.includes("#")) {
          e.preventDefault();
          navigate(item.to);
        }
        onNavigate?.();
      }}
      data-testid={item.testId}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
          isActive
            ? "text-primary-foreground bg-primary shadow-[0_4px_20px_rgba(0,229,255,0.25)]"
            : "text-zinc-400 hover:text-foreground hover:bg-white/5"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <item.icon className="w-4 h-4 shrink-0" strokeWidth={1.75} />
          <span className="flex-1 font-medium">{item.label}</span>
          {isActive && <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground/80" />}
        </>
      )}
    </NavLink>
  );

  return (
    <div className="h-full flex flex-col bg-[#0A0A0E]" data-testid="sidebar">
      {/* Brand */}
      <div className="px-5 py-5" data-testid="sidebar-brand">
        <div className="text-right" dir="ltr">
          <div className="inline-block text-left">
            <div className="text-lg font-bold tracking-tight leading-none" style={{ letterSpacing: "-0.02em" }}>
              <span className="text-foreground">Opty</span>
              <span className="text-primary">go</span>
              <span className="text-zinc-300 font-medium ms-1.5">Pulse</span>
            </div>
            <div className="text-[10px] text-zinc-500 tracking-[0.18em] font-mono mt-1.5 uppercase">
              Smart Business Insights
            </div>
          </div>
        </div>

        {/* Cmd+K trigger */}
        <CommandKBadge className="w-full mt-4 justify-between" />
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 space-y-1 overflow-y-auto rail-scroll">
        {NAV_ITEMS.map(renderItem)}

        {/* Coming-soon collapsible group */}
        <button
          type="button"
          onClick={() => setSoonOpen((v) => !v)}
          data-testid="sidebar-coming-soon-toggle"
          className="w-full mt-2 flex items-center gap-3 px-3 py-2 rounded-md text-[12px] text-zinc-600 hover:text-zinc-400 hover:bg-white/[0.03] transition-colors"
        >
          <ChevronDown
            className={`w-3 h-3 shrink-0 transition-transform ${soonOpen ? "" : "-rotate-90"}`}
            strokeWidth={2}
          />
          <span className="flex-1 text-right font-medium">{COMING_SOON_ITEMS.length} מודולים בקרוב</span>
          <span className="text-[9px] font-mono tracking-widest uppercase text-zinc-700">Q3</span>
        </button>

        {soonOpen && (
          <div className="space-y-0.5 mt-1 mb-2">
            {COMING_SOON_ITEMS.map((item) => (
              <div
                key={item.to}
                data-testid={item.testId}
                className="flex items-center gap-3 px-3 py-1.5 rounded-md text-[12px] text-zinc-700 cursor-not-allowed"
              >
                <item.icon className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                <span className="flex-1">{item.label}</span>
              </div>
            ))}
          </div>
        )}

        {owner && (
          <>
            <div className="h-px bg-white/[0.05] my-3 mx-2" />
            <NavLink
              to="/admin"
              data-testid="nav-admin"
              onClick={() => onNavigate?.()}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-all duration-200 ${
                  isActive
                    ? "text-primary-foreground bg-primary shadow-[0_4px_20px_rgba(0,229,255,0.25)]"
                    : "text-zinc-400 hover:text-foreground hover:bg-white/5"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4 shrink-0" strokeWidth={1.75} />
              <span className="flex-1 font-medium">Admin</span>
              <span className="text-[9px] text-zinc-600 font-mono uppercase tracking-wider">
                private
              </span>
            </NavLink>
          </>
        )}
      </nav>

      {/* Pulse Score — global health vital, always visible from any page */}
      <div className="px-3 pb-2">
        <PulseScoreBadge
          variant="sidebar"
          className="w-full"
          onClick={() => {
            onNavigate?.();
            navigate("/insights");
          }}
        />
      </div>

      {/* Profile */}
      <div className="p-3">
        <div className="flex items-center gap-3 px-2 py-2 rounded-md bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors">
          <div className="relative w-9 h-9 rounded-full bg-gradient-to-br from-primary/30 to-primary/5 border border-primary/20 flex items-center justify-center text-primary text-sm font-semibold">
            {(user.name || "?").charAt(0)}
            <span className="absolute -bottom-0.5 -left-0.5 w-2 h-2 bg-severity-anomaly rounded-full border border-[#0A0A0E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium truncate text-foreground flex items-center gap-1.5">
              <span className="truncate">{user.name || "Guest"}</span>
              {user.tenant && (
                <span
                  className={`text-[8px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border shrink-0 ${
                    user.tenant === "mr"
                      ? "bg-amber-500/10 text-amber-300 border-amber-500/30"
                      : "bg-cyan-500/10 text-cyan-300 border-cyan-500/30"
                  }`}
                  data-testid="sidebar-tenant-chip"
                  title={`Tenant: ${user.tenant}`}
                >
                  {user.tenant === "mr" ? "M.R" : "Optygo"}
                </span>
              )}
            </div>
            <div className="text-[10px] text-zinc-500 truncate">
              {owner ? "Owner" : user.company || "Viewer"}
            </div>
          </div>
          <button
            onClick={() => {
              if (window.confirm("להתנתק?")) handleLogout();
            }}
            className="text-zinc-600 hover:text-severity-critical transition-colors p-1.5"
            title="התנתק"
            data-testid="logout-btn"
          >
            <LogOut className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
